/**
 * Find where a passage of a document a value came from sits in the page text – robust to line breaks, hyphenation,
 * casing and OCR noise. Returns ranges in the *original* string so callers can mark them.
 */
const WORD = /[\p{L}\p{N}]/u;

/** Lower-cased, one space between words; `map[i]` is the original index of normalized char i. */
export function normalize(s) {
  const src = String(s || "").normalize("NFKC"); // ligatures like ﬂ → fl, so PDF text and extracted text agree
  let norm = "";
  const map = [];
  let space = true;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (WORD.test(ch)) {
      norm += ch.toLowerCase();
      map.push(i);
      space = false;
    } else if (!space) {
      norm += " ";
      map.push(i);
      space = true;
    }
  }
  return { norm: norm.trimEnd(), map };
}

/** Best hit for one normalized query in normalized text: whole query, else the longest run of its words (≥4) that occurs. */
function bestIn(norm, nq) {
  const full = norm.indexOf(nq);
  if (full >= 0) return { start: full, end: full + nq.length, score: nq.length + 1000 };
  const words = nq.split(" ").filter((w) => w.length > 0);
  for (let len = Math.min(14, words.length); len >= 4; len--) {
    for (let i = 0; i + len <= words.length; i++) {
      const win = words.slice(i, i + len).join(" ");
      const at = norm.indexOf(win);
      if (at >= 0) return { start: at, end: at + win.length, score: len * 6 + win.length / 100 };
    }
  }
  return null;
}

const toOriginal = (map, hit) => ({ start: map[hit.start], end: (map[hit.end - 1] ?? map[map.length - 1]) + 1, score: hit.score });

/** One hit per query (the best each), non-overlapping, in document order – so title and description both get marked. */
export function findMatches(text, queries) {
  const { norm, map } = normalize(text);
  if (!norm) return [];
  const out = [];
  for (const q of (queries || []).filter(Boolean)) {
    const nq = normalize(q).norm.trim();
    if (nq.length < 4) continue;
    const hit = bestIn(norm, nq);
    if (!hit || out.some((o) => hit.start < o.end && o.start < hit.end)) continue;
    out.push(hit);
  }
  return out.sort((a, b) => a.start - b.start).map((h) => toOriginal(map, h));
}

/** The single strongest hit of any query. */
export function findMatch(text, queries) {
  const all = findMatches(text, queries);
  if (!all.length) return null;
  return all.reduce((b, h) => (h.score > b.score ? h : b));
}

/** Same idea over OCR words: for each query, the run of word indices that matches. */
export function findWordRuns(words, queries) {
  const toks = words.map((w) => normalize(w.t).norm.replace(/\s+/g, ""));
  const starts = [];
  let pos = 0;
  toks.forEach((t) => { starts.push(pos); pos += t.length + 1; });
  const joined = toks.join(" ");
  const runs = [];
  for (const m of findMatches(joined, queries)) {
    const first = starts.findIndex((s, i) => s <= m.start && m.start < s + toks[i].length + 1);
    if (first < 0) continue;
    let last = first;
    for (let i = first; i < starts.length; i++) { if (starts[i] < m.end) last = i; else break; }
    runs.push({ first, last });
  }
  return runs;
}

export function findWordRun(words, queries) {
  return findWordRuns(words, queries)[0] || null;
}

/** A readable excerpt around a match: whole lines where possible, trimmed to ~`around` chars each side. */
export function excerptAround(text, m, around = 220) {
  if (!text) return null;
  if (!m) return { before: "", hit: "", after: text.slice(0, around * 2).trim(), partial: text.length > around * 2 };
  let a = Math.max(0, m.start - around);
  let b = Math.min(text.length, m.end + around);
  const nl = text.lastIndexOf("\n", m.start);
  if (nl >= 0 && m.start - nl < around) a = nl + 1;
  const nn = text.indexOf("\n", m.end);
  if (nn >= 0 && nn - m.end < around) b = nn;
  return { before: text.slice(a, m.start).replace(/^\s+/, ""), hit: text.slice(m.start, m.end), after: text.slice(m.end, b).replace(/\s+$/, ""), partial: a > 0 || b < text.length };
}
